// Shapes mirror apps/server DTOs (see apps/server/src/modules/{auth,dialogs,channels,leads}/*.ts).
// Kept local to apps/web per the task boundary: apps/server is not modified from here.

export type Role = 'owner' | 'lead' | 'manager' | 'reviewer';
export type ChannelType = 'vk_group' | 'vk_personal';
export type ChannelStatus = 'onboarding' | 'onboarding_blocked' | 'active' | 'restricted' | 'disabled';
export type DialogState = 'AGENT_ACTIVE' | 'HUMAN_ACTIVE' | 'CLOSED';
export type SlaStatus = 'ok' | 'warn' | 'breach' | 'escalated';
export type MessageAuthor = 'client' | 'agent' | 'human' | 'system';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
}

export interface SessionWorkspace {
  id: string;
  name: string;
}

export interface MeResponse {
  user: SessionUser;
  workspace: SessionWorkspace;
  role: Role;
  channelIds: readonly string[] | 'all';
  /** Signed in with a temporary password: only the forced password change is available. */
  mustChangePassword?: boolean;
}

export interface LoginResponse extends MeResponse {
  csrf_token: string;
}

export interface DialogContact {
  id: string;
  vkUserId: number;
  firstName: string | null;
  lastName: string | null;
  funnelStage: string;
  tags: string[];
  /** TODO(server): not returned by GET /dialogs yet — render falls back to initials until it lands. */
  photoUrl?: string | null;
}

export interface DialogLastMessage {
  id: string;
  author: string;
  text: string | null;
  createdAt: string;
}

export interface DialogDto {
  id: string;
  channelId: string;
  channelType: ChannelType;
  contact: DialogContact;
  state: DialogState;
  stateReason: string | null;
  stateChangedAt: string;
  assigneeUserId: string | null;
  agentId: string | null;
  slaStatus: SlaStatus;
  awaitingHuman: boolean;
  clientLastMessageAt: string | null;
  humanLastReplyAt: string | null;
  agentLastReplyAt: string | null;
  lastMessage: DialogLastMessage | null;
  version: number;
  updatedAt: string;
}

export interface MessageDto {
  id: string;
  dialogId: string;
  author: MessageAuthor;
  direction: 'in' | 'out';
  text: string | null;
  status: string;
  source: string;
  humanUserId: string | null;
  vkMessageId: number | null;
  sentAt: string | null;
  createdAt: string;
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

/** U5.4 — channel sending limits, in the normal form the API accepts and returns. */
export interface ChannelLimits {
  /** Rolling window length, in hours. */
  window_hours: number;
  /** Max agent sends inside that window. */
  window_max: number;
  /** Calendar-day cap; `null` = no daily cap. */
  daily_max: number | null;
  /** Rolling 7-day cap; `null` = no weekly cap. */
  weekly_max: number | null;
  /** Days after connect during which caps are multiplied by `warmupFactor`. */
  warmup_days: number;
  /** Warm-up multiplier, 0 < f <= 1. */
  warmup_factor: number;
}

/** Answer of `POST /api/v1/channels/resolve` — the preview card before connecting. */
export interface ResolvedChannel {
  type: 'user' | 'group';
  vk_id: number;
  name: string;
  screen_name: string | null;
  photo_url: string | null;
  already_connected: boolean;
  existing_channel_id: string | null;
}

export interface ChannelDto {
  id: string;
  type: ChannelType;
  vkGroupId: number | null;
  vkUserId: number | null;
  title: string;
  status: ChannelStatus;
  statusReason: string | null;
  onboardingUntil: string | null;
  canSend: boolean;
  limits: Record<string, unknown>;
  /** The same limits in normal form — what the limits form binds to (U5.4). */
  limitsNormalized: ChannelLimits;
  /** VK screen name, so the UI can link to `vk.com/<screenName>`. */
  screenName: string | null;
  /** Cached VK avatar (photo_100), refreshed daily. */
  photoUrl: string | null;
  ownerUserId: string | null;
  /** vk_personal only: set once an owner/lead confirmed the binding. */
  confirmedAt: string | null;
  /** vk_personal only: the vk_group channel this personal account works for. */
  linkedGroupChannelId: string | null;
  createdAt: string;
}

export interface DialogListFilters {
  channel_id?: string;
  state?: DialogState;
  stage?: string;
  agent_id?: string;
  awaiting_human?: 'true' | 'false';
  sla?: SlaStatus;
  tag?: string;
  cursor?: string;
  limit?: number;
}

export interface ApiErrorShape {
  /** Some errors carry extras beyond code/message (e.g. `openDialogs`). */
  error: { code: string; message: string } & Record<string, unknown>;
}

export const FUNNEL_STAGES = [
  'new',
  'first_touch',
  'replied',
  'qualified',
  'meeting_requested',
  'meeting_held',
  'kotbot_registered',
  'first_deposit',
  'active',
  'lost',
] as const;
export type FunnelStage = (typeof FUNNEL_STAGES)[number];

export const FUNNEL_STAGE_LABELS: Record<FunnelStage, string> = {
  new: 'Новый',
  first_touch: 'Первый контакт',
  replied: 'Ответил',
  qualified: 'Квалифицирован',
  meeting_requested: 'Запрос на встречу',
  meeting_held: 'Встреча состоялась',
  kotbot_registered: 'Регистрация в kotbot',
  first_deposit: 'Первый депозит',
  active: 'Активен',
  lost: 'Потерян',
};

export type LostReason = 'not_interested' | 'unsubscribed' | 'no_response' | 'bad_fit' | 'other';

export const LOST_REASON_LABELS: Record<LostReason, string> = {
  not_interested: 'Не заинтересован',
  unsubscribed: 'Отписался',
  no_response: 'Нет ответа',
  bad_fit: 'Не подходит',
  other: 'Другое',
};

export interface ContactDto {
  id: string;
  vkUserId: number;
  firstName: string | null;
  lastName: string | null;
  screenName: string | null;
  photoUrl: string | null;
  sourceChannelId: string | null;
  sourceId: string | null;
  /** Denormalized dictionary title (falls back to the legacy free-text source for older rows). */
  sourceTitle: string | null;
  segment: string | null;
  tags: string[];
  ownerUserId: string | null;
  funnelStage: string;
  stageChangedAt: string | null;
  lostReason: string | null;
  doNotContact: boolean;
  kotbotBadge: string | null;
  createdAt: string;
  updatedAt: string;
  /** TODO(server): GET /contacts (list) has no channel type/last message/awaiting-human — kanban cards
   *  show these only when a future response includes them. channelType can be cross-resolved today via
   *  sourceChannelId + GET /channels, which KanbanCard does. */
  channelType?: ChannelType | null;
  lastMessage?: DialogLastMessage | null;
  awaitingHuman?: boolean;
}

export interface ContactListFilters {
  q?: string;
  stage?: string;
  tag?: string;
  owner?: string;
  channel_id?: string;
  source_id?: string;
  kotbot?: string;
  cursor?: string;
  limit?: number;
}

/** U3.4: fixed set — manual override until the kotbot API integration lands (see server-side doc comment). */
export const KOTBOT_BADGE_VALUES = ['not_registered', 'registered_no_deposit', 'active', 'has_blocks'] as const;
export type KotbotBadgeValue = (typeof KOTBOT_BADGE_VALUES)[number];

export const KOTBOT_BADGE_LABELS: Record<KotbotBadgeValue, string> = {
  not_registered: 'Не зарегистрирован',
  registered_no_deposit: 'Зарегистрирован без пополнения',
  active: 'Активен',
  has_blocks: 'Есть блокировки',
};

export function kotbotBadgeLabel(value: string | null): string {
  if (!value) return '—';
  return KOTBOT_BADGE_LABELS[value as KotbotBadgeValue] ?? value;
}

/** Tone for the kotbot badge tag (see BadgeTone in components/ui/badge.tsx — kept as a string here to avoid a circular import). */
export function kotbotBadgeTone(value: string | null): 'neutral' | 'success' | 'warning' | 'danger' {
  switch (value) {
    case 'active':
      return 'success';
    case 'registered_no_deposit':
      return 'warning';
    case 'has_blocks':
      return 'danger';
    default:
      return 'neutral';
  }
}

export interface ContactSourceDto {
  id: string;
  title: string;
  isBuiltin: boolean;
}

export interface ConsentDto {
  id: string;
  channelId: string;
  status: string;
  basis: string;
  grantedAt: string | null;
  revokedAt: string | null;
  revokeReason: string | null;
}

export interface MemoryDto {
  summary: string | null;
  facts: unknown;
  objections: unknown;
  agreements: unknown;
  version: number;
  updatedAt: string;
}

export interface RecentEventDto {
  id: number;
  type: string;
  payload: unknown;
  actor: string | null;
  at: string;
}

export interface ActiveDialogDto {
  id: string;
  channelId: string;
  state: string;
  slaStatus: string;
  updatedAt: string;
}

export interface ContactDetailDto {
  contact: ContactDto;
  consents: ConsentDto[];
  memory: MemoryDto | null;
  recentEvents: RecentEventDto[];
  activeDialogs: ActiveDialogDto[];
}

export interface ContactNoteDto {
  id: string;
  contactId: string;
  authorUserId: string;
  text: string;
  createdAt: string;
}

export interface ImportResult {
  jobId: string;
  status: string;
  total: number;
  processed: number;
  created: number;
  duplicates: number;
  denied: number;
  errors: unknown[];
}

export interface AgentDto {
  id: string;
  name: string;
  status: string;
  persona_id: string | null;
  scenario_id: string | null;
  model_config: Record<string, unknown> | null;
  channel_ids: string[];
  updated_at: string;
}

export interface ScenarioVersionSummary {
  id: string;
  version: number;
  status: string;
}

export interface ScenarioDto {
  id: string;
  name: string;
  active_version_id: string | null;
  created_at: string;
  versions: ScenarioVersionSummary[];
}

export interface ScenarioVersionDetail {
  id: string;
  scenario_id: string;
  version: number;
  status: string;
  definition: Record<string, unknown>;
  simulation_results: SimulationRun[] | null;
  created_at: string;
}

export interface ValidationIssue {
  path?: string;
  message: string;
  [key: string]: unknown;
}

export interface ValidateResult {
  ok: boolean;
  issues: ValidationIssue[];
}

export interface SimulationTranscriptLine {
  role: string;
  text: string;
}

export interface SimulationRun {
  turns: number;
  goalReached: boolean;
  guardrailFail: boolean;
  reason: string | null;
  transcript: SimulationTranscriptLine[];
}

export interface SimulateResult {
  runs: SimulationRun[];
  clean_runs: number;
  total_runs: number;
}

export interface TeamMemberDto {
  userId: string;
  email: string;
  name: string;
  role: Role;
  channelIds: string[];
  createdAt: string;
  disabledAt?: string | null;
  mustChangePassword?: boolean;
}

export type LoginEventResult = 'success' | 'bad_password' | 'disabled' | 'rate_limited';

export interface LoginEventDto {
  id: number;
  userId: string | null;
  name: string | null;
  email: string;
  result: LoginEventResult;
  ip: string | null;
  userAgent: string | null;
  at: string;
}

export interface OwnSessionDto {
  id: string;
  current: boolean;
  created_at: string;
  last_seen_at: string;
  expires_at: string;
  ip: string | null;
  user_agent: string | null;
}

export interface OwnExtensionDto {
  id: string;
  connected_at: string;
  last_used_at: string | null;
  ext_version: string | null;
  user_agent: string | null;
}

export interface SlaSettings {
  warn_min: number;
  repeat_min: number;
  escalate_min: number;
  human_idle_min: number;
  working_hours: { tz: string; from: string; to: string };
}

export interface TelegramSettings {
  leads_chat_id: number | null;
  alerts_chat_id: number | null;
  /** Never the real token — just enough to show it was saved ("123456:AA…XYZ"). */
  bot_token_masked: string | null;
  bot_token_set: boolean;
  bot_username: string | null;
  bot_name: string | null;
  /** Set once "Проверить связь" succeeds; cleared whenever the token changes. */
  verified_at: string | null;
}

export interface TelegramBoundChat {
  role: 'leads' | 'alerts' | 'team';
  chat_id: number;
  label: string;
  team_id?: string;
}

export interface SheetsSettings {
  spreadsheet_id: string | null;
  method: 'service_account' | 'oauth';
  service_account_email: string | null;
  service_account_key_set: boolean;
  oauth_connected: boolean;
  verified_at: string | null;
}

export interface WorkspaceSettings {
  name?: string;
}

export interface AuditEntry {
  id: number;
  workspaceId: string;
  actorUserId: string | null;
  action: string;
  resourceType: string;
  resourceId: string | null;
  before: unknown;
  after: unknown;
  ip: string | null;
  userAgent: string | null;
  at: string;
}

export interface AuditPage {
  items: AuditEntry[];
  nextCursor: string | null;
}

export interface StageConversion {
  from: string;
  to: string;
  rate: number | null;
}

export interface FunnelBucket {
  key: string | null;
  touched: number;
  sent: number;
  delivered: number;
  replied: number;
  qualified: number;
  meetingRequested: number;
  meetingHeld: number;
  unsubscribed: number;
  conversions: StageConversion[];
}

export interface FunnelResponse {
  from: string;
  to: string;
  groupBy: 'channel' | 'agent' | 'scenario_version' | null;
  buckets: FunnelBucket[];
}

export interface ChannelUsageCounter {
  used: number;
  human: number;
  max: number | null;
  ratio: number | null;
}

export interface ChannelUsageDto {
  warmup: boolean;
  warmupEndsAt: string;
  window: ChannelUsageCounter;
  daily: ChannelUsageCounter;
  weekly: ChannelUsageCounter;
}

export interface ChannelHealthItem {
  channelId: string;
  title: string | null;
  status: string;
  usage: ChannelUsageDto;
  warmupDaysRemaining: number;
  incidents7d: number;
  incidents30d: number;
  unsubscribeRate24h: number;
  dialogs24h: number;
}

export interface ChannelHealthResponse {
  items: ChannelHealthItem[];
}

export interface SlaMetricsResponse {
  from: string;
  to: string;
  dialogsInPeriod: number;
  dialogsWithHandoff: number;
  shareWithHandoff: number | null;
  violations: number;
  violationRate: number | null;
  medianResponseMs: number | null;
  p90ResponseMs: number | null;
  responseSampleSize: number;
}

export interface ChannelHealthDetail {
  channelId: string;
  status: string;
  statusReason: string | null;
  floodStrikes: number;
  usage: ChannelUsageDto;
  incidents: { last7Days: unknown[]; last30Days: unknown[] };
}
